import { useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LangLink as Link } from "@/components/LangLink";
import { ENABLE_PT } from "@/config";

import logo from "@/assets/bizu-logo.png";
import hamburgerIcon from "@/assets/hamburguer.png";
import flagUs from "@/assets/flag-us.png";
import flagBrazil from "@/assets/flag-brazil.png";

type NavItem = {
  label: string;
  to: string;
  dropdown?: ReadonlyArray<{ name: string; to: string; key: string }>;
};

const navItems: ReadonlyArray<NavItem> = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },

  {
    label: "Products",
    to: "/products",
    dropdown: [
      { name: "Propulsion Systems", to: "/category/propulsion-systems", key: "propulsion" },
      { name: "Chemicals", to: "/category/chemicals", key: "chemicals" },
      { name: "Electronic Systems", to: "/category/eletronic-systems", key: "electronics" },
      { name: "CubeSat Deployer", to: "/category/cubesat-deployer-system", key: "cubesat" },
    ],
  },
  {
    label: "Services",
    to: "/services",
    dropdown: [
      { name: "Aerospace and Systems Engineering", to: "/services/aerospace", key: "aerospace" },
      { name: "Aerospace Manufacturing", to: "/services/manufacturing", key: "manufacturing" },
      { name: "T8 Test Facility", to: "/services/t8", key: "t8" },
      { name: "Launch Services", to: "/services/ftl", key: "launch" },
    ],
  },
  { label: "Missions", to: "/missions" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
  { label: "Store", to: "https://montink.com/bizu-space-store/" },
];

const mobileLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/missions", label: "Missions" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
  { to: "https://montink.com/bizu-space-store/", label: "Store" },
] as const;

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const { t } = useTranslation("common");

  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled
          ? "bg-black/55 backdrop-blur-3xl border-b border-border/50"
          : "bg-black/20 backdrop-blur-sm"
          }`}
      >
        <nav className="container-bizu flex items-center justify-between py-4 lg:py-0">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="BIZU Space" className="h-auto w-[100px]" width={400} height={120} />
          </Link>

          <ul className="hidden lg:flex items-center gap-8">
            {navItems.map((item, index) => {
              const isActive =
                location.pathname === item.to ||
                item.dropdown?.some((sub) => location.pathname === sub.to.split("#")[0]);
              const label = t(`nav.${item.label.toLowerCase()}`, item.label);

              return (
                <li key={item.label} className="relative">
                  <div className="group relative py-6">
                    {item.to.startsWith("http") ? (
                      <a
                        href={item.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-[20px] font-bold text-white transition hover:text-accent"
                      >
                        {label}
                      </a>
                    ) : (
                      <>
                        <Link
                          to={item.to as any}
                          className="flex items-center gap-3 text-[20px] font-bold text-white transition"
                        >
                          {label}
                        </Link>

                        {item.dropdown && (
                          <div className="pointer-events-none absolute left-1/2 top-full z-50 w-[320px] -translate-x-1/2 translate-y-0 bg-[#111] opacity-0 shadow-2xl transition-all duration-300 group-hover:pointer-events-auto group-hover:opacity-100">
                            <div className="h-[3px] w-full bg-accent" />

                            {item.dropdown.map((sub) => (
                              <Link
                                key={sub.to}
                                to={sub.to as any}
                                className="block border-b border-white/10 px-4 py-3 text-center text-[16px] font-bold text-white transition hover:bg-white/[0.04] hover:text-accent last:border-b-0"
                              >
                                {t(`nav.dropdown.${sub.key}`, sub.name)}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    <span
                      className={`absolute -bottom-1 left-1/2 h-[3px] w-[84px] -translate-x-1/2 bg-accent transition-all duration-300 ${isActive
                        ? "scale-x-100 opacity-100"
                        : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
                        }`}
                    />
                  </div>

                  {index !== navItems.length - 1 && <span className="h-10 w-px bg-white/60" />}
                </li>
              );
            })}
          </ul>

          {ENABLE_PT && (
            <div className="hidden lg:flex items-center gap-3 text-lg">
              <Link
                to="."
                params={{ lang: "en" }}
                resetScroll={false}
                className="opacity-80 hover:opacity-100 transition cursor-pointer"
                aria-label="English"
              >
                <img src={flagUs} alt="" className="h-7 w-7 object-contain" />
              </Link>
              <span className="text-white/25">/</span>
              <Link
                to="."
                params={{ lang: "pt" }}
                resetScroll={false}
                className="opacity-80 hover:opacity-100 transition cursor-pointer"
                aria-label="Português"
              >
                <img src={flagBrazil} alt="" className="h-7 w-7 object-contain" />
              </Link>
            </div>
          )}

          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="relative z-50 flex h-11 w-11 items-center justify-center lg:hidden"
          >
            <img src={hamburgerIcon} alt="" className="h-7 w-7 object-contain" />
          </button>
        </nav>
      </header>

      <div
        className={`fixed inset-0 z-[999] bg-black transition-all duration-700 lg:hidden ${open
          ? "pointer-events-auto translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-full opacity-0"
          }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-4 py-4">
            <Link to="/" onClick={() => setOpen(false)}>
              <img src={logo} alt="BIZU Space" className="h-14 w-auto brightness-0 invert" width={400} height={120} />
            </Link>

            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="relative h-12 w-12"
            >
              <span className="absolute left-1/2 top-1/2 h-[2px] w-9 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white" />
              <span className="absolute left-1/2 top-1/2 h-[2px] w-9 -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-white" />
            </button>
          </div>

          <nav className="flex flex-1 items-center justify-center">
            <ul className="space-y-5 text-center">
              {mobileLinks.map((link, index) => {
                const label = t(`nav.${link.label.toLowerCase()}`, link.label);

                return (
                  <li key={link.to}>
                    {link.to.startsWith("http") ? (
                      <a
                        href={link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="text-[32px] font-light leading-tight text-white/75 transition hover:text-white"
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        to={link.to as any}
                        onClick={() => setOpen(false)}
                        className="text-[32px] font-light leading-tight text-white/75 transition hover:text-white"
                      >
                        {label}
                      </Link>
                    )}
                  </li>
                );
              })}

              {ENABLE_PT && (
                <li className="pt-4">
                  <div className="flex items-center justify-center gap-4 text-3xl">
                    <Link
                      to="."
                      params={{ lang: "en" }}
                      resetScroll={false}
                      onClick={() => setOpen(false)}
                      className="opacity-80"
                      aria-label="English"
                    >
                      <img src={flagUs} alt="" className="h-7 w-7 object-contain" />
                    </Link>
                    <span className="text-white/25">/</span>
                    <Link
                      to="."
                      params={{ lang: "pt" }}
                      resetScroll={false}
                      onClick={() => setOpen(false)}
                      className="opacity-80"
                      aria-label="Português"
                    >
                      <img src={flagBrazil} alt="" className="h-7 w-7 object-contain" />
                    </Link>
                  </div>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}
